import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { sprekersApi } from '../api';
import type { CanvasTemplateSummary } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (templateRef: string) => Promise<void>;
}

/** Grid van kaarten (ingebouwde kerkgerichte starttemplates + organisatie-
 * eigen opgeslagen templates) -- "Toepassen" vervangt het HUIDIGE board
 * volledig (zelfde replace-all als JSON-import), vandaar de bevestiging. */
export default function CanvasTemplatePickerDialog({ open, onOpenChange, onApply }: Props) {
  const [templates, setTemplates] = useState<CanvasTemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    sprekersApi.listCanvasTemplates()
      .then(setTemplates)
      .catch(() => toast.error('Kon sjablonen niet laden'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleApply = async (template: CanvasTemplateSummary) => {
    if (!window.confirm(`"${template.name}" toepassen vervangt het huidige canvas volledig. Doorgaan?`)) return;
    setApplyingId(template.id);
    try {
      await onApply(template.id);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Toepassen mislukt');
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Sjabloon toepassen</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-slate-500">Laden...</p>}
        {!loading && templates.length === 0 && <p className="text-sm text-slate-500">Nog geen sjablonen.</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {templates.map((template) => (
            <div key={template.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-slate-900">{template.name}</p>
                {template.is_builtin && <Badge className="bg-teal-100 text-teal-700">Ingebouwd</Badge>}
              </div>
              {template.description && <p className="mt-1 text-sm text-slate-500">{template.description}</p>}
              <Button
                type="button" size="sm" className="mt-2" disabled={applyingId === template.id}
                onClick={() => handleApply(template)}
              >
                Toepassen
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
